import { useQuery } from "@tanstack/react-query";
import { API_URL } from "@/config/api";

export interface PublicSettings {
  maintenanceMode: boolean;
  maintenanceMessage: string;
  customerCanEditActiveOrder: boolean;
  /** Minutes before the appointment after which editing closes. 0 = no cutoff. */
  customerEditCutoffMinutes: number;
  customerCanCancelOrder: boolean;
  /** Minutes before the appointment after which cancelling closes. 0 = no cutoff. */
  customerCancelCutoffMinutes: number;
  /** Shop delivery options (admin-managed) — absent until settings load. */
  productDeliveryPeriods?: Array<{
    labelEn: string;
    labelAr: string;
    startTime: string;
    endTime: string;
    active: boolean;
  }>;
  productDeliveryDaysAhead?: number;
}

/** Matches the server default when settings haven't loaded yet. */
export const DEFAULT_EDIT_CUTOFF_MINUTES = 60;

/**
 * Fetch public site settings (maintenance flag, etc.) — no auth required.
 * Refetched on window focus so the customer picks up a maintenance toggle
 * shortly after the admin flips it.
 */
export function usePublicSettings() {
  return useQuery<PublicSettings>({
    queryKey: ["/api/settings/public"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/settings/public`);
      if (!res.ok) {
        // Fail open — never block the site due to a settings fetch error.
        return {
          maintenanceMode: false,
          maintenanceMessage: "",
          customerCanEditActiveOrder: false,
          customerEditCutoffMinutes: DEFAULT_EDIT_CUTOFF_MINUTES,
          customerCanCancelOrder: false,
          customerCancelCutoffMinutes: DEFAULT_EDIT_CUTOFF_MINUTES,
        };
      }
      return res.json();
    },
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });
}
